/* eslint-disable line-comment-position, no-inline-comments */

import {
  Leg,
  PTRideLeg,
  Trip,
  TripResponse,
} from '../../../api/clients/journey-service';
import { InterfaceResultTimetableRow } from '../../../components/paf-result-timetable-row/paf-result-timetable-row.custom';
import { InterfaceUtilization } from '../../../components/paf-result-utilization-icon/paf-result-utilization-icon.custom';
import { InterfacePearlChainLeg } from '../../../components/paf-result-pearl-chain/paf-result-pearl-chain.custom';
import { InterfaceTimetableResults } from '../paf-timetable.custom';

import { formatISO } from 'date-fns/formatISO';
import { format } from 'date-fns/format';

import {
  formatDateStringIncludingDayName,
  isoDurationToTripDuration,
  isoDurationToWalkDuration,
  isoDurationToWalkDurationFormatted,
} from '../../../utils/date-helper';
import {
  Link,
  TripPrice,
} from '../../../api/clients/smapi-ticketing';
import * as levelHelpers from './levelsHelpers';
import { InterfaceCusHimMessages } from '../../../components/paf-result-timetable-leg/paf-result-timetable-leg.custom';
import { InterfaceI18nConfig } from '../i18n/i18n.interface';

const getHighestOccupancyForTrip = (legs: PTRideLeg[], classKey: string): string => {
  let index = 0;

  legs.forEach((leg) => {
    const legIndex = levelHelpers.getHighestOccupancyForLeg(leg, classKey);
    const currentIndex = levelHelpers.occupancyStrings.indexOf(legIndex);

    if (currentIndex > index) {
      index = currentIndex;
    }

  });

  return levelHelpers.occupancyStrings[index];
};

const getOccupancy = (_occupancy: string): InterfaceUtilization['property'] => {
  if (_occupancy === 'UNKNOWN') {
    return false;
  }

  if (_occupancy === 'LOW') {
    return 'low';
  }

  if (_occupancy === 'MEDIUM') {
    return 'medium';
  }

  if (_occupancy === 'HIGH') {
    return 'high';
  }

  return false;
};

const getServiceAlteration = (trip: Trip): InterfaceCusHimMessages => {

  const rideLegs = levelHelpers.getRideLegs(trip.legs);

  // get cus/him for all legs
  let alterations: InterfaceCusHimMessages[] = [];

  rideLegs.forEach((leg) => {
    const cusHimForLeg = levelHelpers.getCusHimMessagesForLeg(leg);

    alterations = alterations.concat(cusHimForLeg);
  });

  // get service alternative
  if (trip.status.alternative && trip.status.alternativeFormatted) {
    alterations.push({
      description: trip.status.alternativeFormatted,
      iconName: 'alternative',
    });
  }

  // get trip cancellation
  if (trip.status.cancelled && trip.status.cancelledFormatted) {
    alterations.push({
      description: trip.status.cancelledFormatted,
      iconName: 'cancellation',
    });
  }

  // dedupe alterations
  const dedupedAlterations: InterfaceCusHimMessages[] = [];

  alterations.forEach((alteration) => {
    let alreadyThere = false;

    dedupedAlterations.forEach((dedupedAlteration) => {
      if (dedupedAlteration.iconName === alteration.iconName) {
        alreadyThere = true;
      }
    });

    if (!alreadyThere) {
      dedupedAlterations.push(alteration);
    }
  });

  // order alterations by priority
  /*
  Hierarchie defined by post:
  - Cancellation
  - Missed connection
  - Platform change
  - Alternative
  - Delay
  - Disruption
  - Info
  - Reroute
  - Add-stop
  - Replacementbus
  - Construction
  */

  const orderedAlterations: InterfaceCusHimMessages[] = [];

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'cancellation') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'missed-connection') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'platform-change') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'alternative') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'delay') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'disruption') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'info') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'reroute') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'add-stop') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'replacementbus') {
      orderedAlterations.push(alteration);
    }
  });

  dedupedAlterations.forEach((alteration) => {
    if (alteration.iconName === 'construction') {
      orderedAlterations.push(alteration);
    }
  });

  if (orderedAlterations.length > 0) {
    return orderedAlterations[0];
  }

  return undefined;

};

const getAttributeIcons = (legs: PTRideLeg[], a11yChecked): string => {
  let reservation = false;
  let bike = false;
  let additionalCharge = false;
  let specialTicket = false;
  const icons = [];

  legs.forEach((leg) => {

    // reservation and bike
    leg.serviceJourney.notices.forEach((notice) => {
      if (notice.name === 'RR') {
        reservation = true;
      }

      if (notice.name === 'VR') {
        bike = true;
      }

      if (notice.name === 'Z') {
        additionalCharge = true;
      }

      if (notice.name === 'SB') {
        specialTicket = true;
      }
    });
  });

  if (reservation) {
    icons.push('sa-rr');
  }

  if (bike) {
    icons.push('sa-vr');
  }

  if (a11yChecked) {
    const topPrioIcon = levelHelpers.getA11yItemFromLegs(legs);

    if (topPrioIcon) {
      icons.push(topPrioIcon.corporateIdentityIcon.toLocaleLowerCase());
    }

  }

  if (additionalCharge) {
    icons.push('sa-z');
  }

  if (specialTicket) {
    icons.push('sa-sb');
  }

  return JSON.stringify(icons.map((item) => ({
    name: item,
  })));
};

const getWalkTime = (leg: Leg): number => {
  if (leg.type !== 'AccessLeg') {
    return 0;
  }

  const {
    duration,
  } = leg;

  if (duration !== undefined) {
    return isoDurationToWalkDuration(duration);
  }

  return 0;

};

const getWalkTimeFormatted = (leg: Leg, language: string): string => {
  if (leg.type !== 'AccessLeg') {
    return '';
  }

  const {
    duration,
  } = leg;

  if (duration !== undefined) {
    return isoDurationToWalkDurationFormatted(duration, language);
  }

  return '';
};

const getDepartureTime = (legs: PTRideLeg[]): string => levelHelpers.getDepartureTimeForLeg(legs[0]);

const getArrivalTime = (legs: PTRideLeg[]): string => {
  const leg = legs[legs.length - 1];

  return levelHelpers.getArrivalTimeTimeForLeg(leg);
};

const renderPearlChain = (legs: PTRideLeg[]): InterfacePearlChainLeg[] => {
  const pearlChainLegs: InterfacePearlChainLeg[] = [];

  legs.forEach((leg) => {
    const {
      stopPoints,
    } = leg.serviceJourney;

    const arrivalTime = stopPoints[stopPoints.length - 1].arrival?.timeAimed;
    const departureTime = stopPoints[0].departure?.timeAimed;

    const arrival = formatISO(arrivalTime);
    const departure = formatISO(departureTime);

    pearlChainLegs.push({
      arrival,
      cancelled: leg.serviceJourney.serviceAlteration.cancelled, // todo: open for testing
      departure,
    });
  });

  return pearlChainLegs;
};

const getNoticesStrings = (legs: PTRideLeg[], a11yChecked: boolean): string => {
  const notices = [];
  let noticesString = '';
  const relevantNoticeNames = [
    'RR', // reservation obligatory
    'VR', // Bike reservation obligatory
  ];

  legs.forEach((leg) => {
    const {
      serviceJourney,
    } = leg;

    serviceJourney.notices.forEach((notice) => {
      if (relevantNoticeNames.includes(notice.name)) {
        notices.push(notice);
      }
    });
  });

  const dedupedNotices = notices.filter((value, index, self) => index === self.findIndex((t) => (t.name === value.name)));

  dedupedNotices.forEach((notice, index) => {
    noticesString += notice.text.template;
    if (index < notices.length - 1) {
      noticesString += ', ';
    }
  });

  // a11y
  if (a11yChecked) {
    const topPrioIcon = levelHelpers.getA11yItemFromLegs(legs);

    if (topPrioIcon) {
      if (noticesString.length > 0) {
        noticesString += ', ';
      }
      noticesString += topPrioIcon.name;
    }
  }

  return noticesString;
};

const getRealtimeInfo = (trip: Trip): string => {
  const alterations: InterfaceCusHimMessages = getServiceAlteration(trip);

  if (!alterations) {
    return '';
  }

  return `${alterations.description}.`;
};

const getOccupancyInfo = (legs: PTRideLeg[], config: InterfaceI18nConfig, language): string => {
  const firstClass = getHighestOccupancyForTrip(legs, 'occupancyFirstClass');
  const secondClass = getHighestOccupancyForTrip(legs, 'occupancySecondClass');

  return levelHelpers.getOccupancyStringForOccupancies(firstClass, secondClass, config, language);
};

const getHeading = (configParsed: InterfaceI18nConfig, rideLegs: PTRideLeg[], language: string): string => {
  const templateTexts = configParsed.results.heading[language];
  const departurePlatform = levelHelpers.getDeparturePlatformForLeg(rideLegs[0]);

  let headingText = templateTexts
    .replace('{{1}}', format(getDepartureTime(rideLegs), 'HH:mm'));

  if (departurePlatform) {
    headingText = headingText
      .replace('{{2}}', `${departurePlatform}, `);
  } else {
    headingText = headingText
      .replace('{{2}}', '');
  }

  headingText = headingText
    .replace('{{3}}', levelHelpers.getTransportationNameForLeg(rideLegs[0]))
    .replace('{{4}}', levelHelpers.getProductTextForLeg(rideLegs[0]))
    .replace('{{5}}', levelHelpers.getDirectionForLeg(rideLegs[0], configParsed, language));

  return headingText;
};

const renderSummaryGeneral = (configParsed: InterfaceI18nConfig, rideLegs: PTRideLeg[], trip: Trip, language: string): string => {
  const templateTexts = configParsed.results.hiddenRowSummary;

  const walkTimeDeparture = getWalkTimeFormatted(trip.legs[0], language);
  const walkTimeArrival = getWalkTimeFormatted(trip.legs[trip.legs.length - 1], language);

  let walkTimeExpressionDeparture = templateTexts.walkTimeDeparture[language].replace('{{1}}', walkTimeDeparture);
  let walkTimeExpressionArrival = templateTexts.walkTimeArrival[language].replace('{{1}}', walkTimeArrival);

  const departurePlatform = levelHelpers.getDeparturePlatformForLeg(rideLegs[0]);

  if (walkTimeDeparture === '') {
    walkTimeExpressionDeparture = '';
  }

  if (walkTimeArrival === '') {
    walkTimeExpressionArrival = '';
  }

  let general = templateTexts.general[language]
    .replace('{{1}}', walkTimeExpressionDeparture)
    .replace('{{2}}', format(getDepartureTime(rideLegs), 'HH:mm'));

  if (departurePlatform) {
    general = general.replace('{{3}}', `${departurePlatform}, `);
  } else {
    general = general.replace('{{3}}', '');
  }

  general = general
    .replace('{{4}}', levelHelpers.getTransportationNameForLeg(rideLegs[0]))
    .replace('{{5}}', levelHelpers.getProductTextForLeg(rideLegs[0]))
    .replace('{{6}}', levelHelpers.getDirectionForLeg(rideLegs[0], configParsed, language))
    .replace('{{7}}', format(getArrivalTime(rideLegs), 'HH:mm'))
    .replace('{{8}}', isoDurationToTripDuration(trip.duration, 'long', language))
    .replace('{{9}}', walkTimeExpressionArrival);

  // price information will be replaced/added in a separate method

  return general;
};

const renderSummaryRealtimeInfo = (configParsed: InterfaceI18nConfig, rideLegs: PTRideLeg[], trip: Trip, language): string => {
  const realTimeInfo = getRealtimeInfo(trip);
  const occupancyInfo = getOccupancyInfo(rideLegs, configParsed, language);

  if (realTimeInfo === '' && occupancyInfo === '') {
    return '';
  }

  const realtimeInfo = configParsed.results.hiddenRowSummary.realtimeInfo[language]
    .replace('{{1}}', realTimeInfo)
    .replace('{{2}}', occupancyInfo);

  return realtimeInfo;
};

const renderSummaryTravelNotices = (configParsed: InterfaceI18nConfig, rideLegs: PTRideLeg[], language, a11yChecked: boolean): string => {
  const legsNotices = getNoticesStrings(rideLegs, a11yChecked);

  if (legsNotices === '') {
    return '';
  }

  const travelNotices = configParsed.results.hiddenRowSummary.travelNotices[language]
    .replace('{{1}}', legsNotices);

  return travelNotices;
};

const renderSummary = (configParsed: InterfaceI18nConfig, rideLegs: PTRideLeg[], trip: Trip, language: string, a11yChecked: boolean): string => {
  const general = renderSummaryGeneral(configParsed, rideLegs, trip, language);
  const realtime = renderSummaryRealtimeInfo(configParsed, rideLegs, trip, language);
  const notices = renderSummaryTravelNotices(configParsed, rideLegs, language, a11yChecked);

  return `${general} ${realtime} ${notices}`;
};

const getBuyLinkFromLinksArray = (links: Link[]): string => {
  let buyHref = '';

  links.forEach((link) => {
    if (link.rel === 'online-offers') {
      buyHref = link.href;
    }
  });

  return buyHref;
};

export const pricePlaceHolder = 'XX.XX';

export const addPriceToHiddenSummary = (
  summary: string,
  config: InterfaceI18nConfig,
  price: string,
  supersaver: boolean,
  language: string,
): string => {
  const templatePrice = config.results.hiddenRowSummary.tickets;
  const templatePriceDiscount = config.results.hiddenRowSummary.discountTickets;
  const template = supersaver
    ? templatePriceDiscount
    : templatePrice;

  let priceString = template[language].replace('{{1}}', price);

  if (!price) {
    priceString = '';
  }

  return summary.replace('{{10}}', priceString);
};

const hasQuayChange = (leg: PTRideLeg): boolean => leg.serviceJourney.stopPoints[0].departure.quayChanged;

const tripHasReservation = (trip: Trip): boolean => {
  let reservationDataTrip;
  const legs = levelHelpers.getRideLegs(trip.legs);

  legs.forEach((leg) => {

    // reservation for notice 'hi' is only obligatory, if also notice
    // 'RR' or 'VR' is present. So we filter out everything except 'RR'.
    // e.g. Sportbus Sörenberg only has 'hi', but no 'RR' notice, so we don't
    // want to show 'Reservation' in buy button, but we want to show the
    // info box on level 2.
    const legReservationObligatory = leg.serviceJourney.notices.filter((item) => item.name === 'RR' || item.name === 'VR');

    if (legReservationObligatory.length > 0) {
      reservationDataTrip = true;
    }
  });

  return reservationDataTrip;
};

const tripHasReservationPossibleNotice = (trip: Trip): boolean => {
  let hasNotice = false;
  const rideLegs = levelHelpers.getRideLegs(trip.legs);

  rideLegs.forEach((leg) => {
    leg.serviceJourney.notices.forEach((notice) => {
      if (notice.name.toLocaleLowerCase() === 'r') {
        hasNotice = true;
      }
    });
  });

  return hasNotice;
};

// TRIPS

export const transformLevelOneData = (trips: TripResponse, a11yChecked: boolean, config: InterfaceI18nConfig, language: string): InterfaceTimetableResults => {
  const transformedTrips: InterfaceResultTimetableRow[] = [];

  trips.trips.forEach((trip) => {
    const rideLegs = levelHelpers.getRideLegs(trip.legs);
    const cusHimIcons = getServiceAlteration(trip);
    const cusHimIconsString = cusHimIcons
      ? JSON.stringify([cusHimIcons.iconName])
      : '';
    const departureDateRaw = rideLegs[0].serviceJourney.stopPoints[0].departure?.timeAimed;
    const departureDate = typeof departureDateRaw === 'string'
      ? departureDateRaw
      : departureDateRaw.toDateString();

    transformedTrips.push({
      date: departureDate,
      heading: getHeading(config, rideLegs, language),
      headingLevel: '4',
      id: trip.id,
      link: {
        aLabelValue: tripHasReservation(trip) || tripHasReservationPossibleNotice(trip)
          ? config.results.buy.withReservation.hiddenText[language]
          : config.results.buy.default.hiddenText[language],
        disabled: false,
        href: '',
        text: tripHasReservation(trip) || tripHasReservationPossibleNotice(trip)
          ? config.results.buy.withReservation.text[language]
          : config.results.buy.default.text[language],
        variant: 'primary',
      },
      pearlChain: {
        legs: JSON.stringify(renderPearlChain(rideLegs)),
        now: (new Date()
          .valueOf()) / 1000,
        orientation: 'horizontal',
      },
      pearlChainTime: {
        arrivalTime: getArrivalTime(rideLegs),
        arrivalWalk: getWalkTime(trip.legs[trip.legs.length - 1]),
        departureTime: getDepartureTime(rideLegs),
        departureWalk: getWalkTime(trip.legs[0]),
        wheelchair: a11yChecked,
      },

      priceBadge: {
        isDiscount: false,
        priceText: `ab CHF ${pricePlaceHolder}`,
      },
      rowFooter: {
        attributeIcons: getAttributeIcons(rideLegs, a11yChecked),
        cusHimIcons: cusHimIconsString,
        duration: isoDurationToTripDuration(trip.duration, 'short', language),
        hasPlatformChange: hasQuayChange(rideLegs[0]),

        // we get the highest occupancy of all legs from a trip
        occupancyClassOne: getOccupancy(getHighestOccupancyForTrip(rideLegs, 'occupancyFirstClass')),
        occupancyClassTwo: getOccupancy(getHighestOccupancyForTrip(rideLegs, 'occupancySecondClass')),
        platform: levelHelpers.getDeparturePlatformForLeg(rideLegs[0]),
      },
      rowHeader: {
        direction: levelHelpers.getDirectionForLeg(rideLegs[0], config, language),
        level: '1',
        picto: levelHelpers.getPictoForLeg(rideLegs[0]),
        productIcon: levelHelpers.getProductIconForLeg(rideLegs[0]),
        productText: levelHelpers.getProductTextForLegOptional(rideLegs[0]),
      },
      summary: renderSummary(config, rideLegs, trip, language, a11yChecked),
    });
  });

  return {
    // paginationCursor seems to be deprecated, but sbb.ch seems uses it.
    // todo: ask sbb if there is a replacement for this feature.
    paginationCursor: trips.paginationCursor,
    results: transformedTrips,

  };
};

// PRICES

interface InterfacePrice {
  price: number;
  supersaver: boolean;
  link: string;
}

type InterfacePrices = Record<string, InterfacePrice>;

export const getCheapestPricePerTrip = (prices: TripPrice[]): InterfacePrices => {
  const pricePerTripId: InterfacePrices = {};

  prices.forEach((price) => {
    if (pricePerTripId[price.tripId]) {
      if (price.price < pricePerTripId[price.tripId].price) {
        pricePerTripId[price.tripId] = {
          link: getBuyLinkFromLinksArray(price.links),
          price: price.price,
          supersaver: price.superSaver,
        };
      }
    } else {
      pricePerTripId[price.tripId] = {
        link: getBuyLinkFromLinksArray(price.links),
        price: price.price,
        supersaver: price.superSaver,
      };
    }
  });

  return pricePerTripId;
};

// DAY CHANGE

type InterfaceDayChangeIndex = Record<string, number>;

const getDayChangeIndexForTrips = (trips: InterfaceResultTimetableRow[], lang: string): InterfaceDayChangeIndex => {
  if (!trips) {
    return {};
  }

  const indexes: InterfaceDayChangeIndex = {};
  let currentDate;

  trips.forEach((trip, index) => {
    const tripDate = formatDateStringIncludingDayName(trip.date, lang);

    if (tripDate !== currentDate) {
      currentDate = tripDate;
      indexes[tripDate] = index;
    }
  });

  return indexes;
};

type InterfaceTripsByDate = Record<string, InterfaceResultTimetableRow[]>;

export const splitTripsByDate = (trips: InterfaceResultTimetableRow[], lang: string): InterfaceTripsByDate => {
  const dayChangeIndexes = getDayChangeIndexForTrips(trips, lang);
  const tripsByDay: InterfaceTripsByDate = {};
  let lastIndex = 0;

  const dayChangeKeys = Object.keys(dayChangeIndexes);

  dayChangeKeys.forEach((date, iterationIndex) => {
    const dayChangeIndex = dayChangeIndexes[date];

    if (iterationIndex < dayChangeKeys.length - 1) {
      // we're not in the last dayChangeIndex

      const nextIndex = dayChangeIndexes[dayChangeKeys[iterationIndex + 1]];
      const dayTrips = trips.slice(lastIndex, nextIndex);

      lastIndex = dayChangeIndex;
      tripsByDay[date] = dayTrips;
    } else {
      // we in the last dayChangeIndex

      const dayTrips = trips.slice(dayChangeIndex, trips.length);

      tripsByDay[date] = dayTrips;
    }
  });

  return tripsByDay;
};
